<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <form @submit.prevent="handleSubmit(submit)">
      <md-card>
        <md-card-header  v-if="formTitle" class="md-card-header-icon md-card-header-rose">
          <div class="card-icon">
            <md-icon>settings</md-icon>
          </div>
            <h4 class="title">{{ formTitle }}</h4>

               <slot name="top"></slot>
        </md-card-header>
        <md-card-content>
          <div v-for="(input, index) in inputs" :key="index" class="field-row" :class="input.type+'-field'">
            <label class="md-layout-item md-form-label">{{ input.label }}</label>
            <div class="md-layout-item md-xsmall-size-100">
              <ValidationProvider
                :name="input.name"
                :rules="getValidationRules(input.name)"
                v-slot="{ passed, failed }"
              >
                <md-field :class="[{ 'md-error': failed }, { 'md-valid': passed }]">
                  <template v-if="input.type === 'image'">
                    <div class="file-input img-circle">
                      <template v-if="!formData[input.name]">
                        <div class="image-container">
                          <img :src="input.placeholder || avatarImg" alt="Image preview" />
                        </div>
                      </template>
                      <div class="image-container" v-else>
                        <img :src="formData[input.name]" alt="Uploaded image" />
                      </div>
                      <div class="button-container">
                        <md-button
                          class="md-danger md-round"
                          v-if="formData[input.name]"
                          @click="removeImage(input.name)"
                        >
                          <i class="fa fa-times"></i> Remove
                        </md-button>
                        <md-button class="md-success md-round md-fileinput" :disabled="isUploading">
                          <template v-if="!formData[input.name]">Select image</template>
                          <template v-else>Change</template>
                          <input type="file" accept="image/png, image/jpeg, image/gif" @change="onFileChange($event, input.name)" />
                        </md-button>
                      </div>
                    </div>
                  </template>
                  <template v-else-if="input.type === 'video'">
                    <div class="file-input">
                      <template v-if="!formData[input.name]">
                        <div class="video-container">
                          <md-icon class="video-placeholder">videocam</md-icon>
                          <p>No video selected</p>
                        </div>
                      </template>
                      <div class="video-container" v-else>
                        <video :src="formData[input.name]" controls width="100%" max-width="300px">
                          Your browser does not support the video tag.
                        </video>
                      </div>
                      
                      <!-- Indicador de carga para video -->
                      <div v-if="isUploading" class="upload-loading">
                        <div class="loading-info">
                          <md-icon class="loading-icon">cloud_upload</md-icon>
                          <span>Subiendo video...</span>
                        </div>
                        <md-progress-bar 
                          :indeterminate="true"
                          class="md-warning"
                        ></md-progress-bar>
                      </div>
                      
                      <div class="button-container">
                        <md-button
                          class="md-danger md-round"
                          v-if="formData[input.name] && !isUploading"
                          @click="removeVideo(input.name)"
                        >
                          <i class="fa fa-times"></i> Quitar
                        </md-button>
                        <md-button class="md-success md-round md-fileinput" :disabled="isUploading">
                          <template v-if="!formData[input.name]">Seleccionar video</template>
                          <template v-else>Cambiar</template>
                          <input type="file" accept="video/mp4, video/webm, video/ogg" @change="onVideoChange($event, input.name)" />
                        </md-button>
                      </div>
                    </div>
                  </template>
                  <template v-else-if="input.type === 'select'"> 

                    <md-select 
                      v-model="formData[input.name]" 
                      :multiple="input.multiple || false" 
                      @md-selected="if(submitonchange){submit()}"
                      v-if="input.options && input.options[0] && input.options[0].value"
                    >
                      <md-option v-for="option in input.options" :key="option.value" :value="option.value">
                        {{ option.text }}
                      </md-option>
                    </md-select>
                    <md-select 
                      v-else
                      v-model="formData[input.name]" 
                      :multiple="input.multiple || false" 
                      @md-selected="if(submitonchange){submit()}"
                    >
                      <md-option v-for="option in input.options" :key="option" :value="option">
                        {{ option }}
                      </md-option>
                    </md-select>
                  </template>
                  
                  <!-- Si el tipo es otro, usamos el input por defecto -->

                  <template v-else>
                    <md-input v-model="formData[input.name]" :type="input.type" />
                  </template>

                  <!-- Mostrar iconos de validación -->
                  <slide-y-down-transition v-if="!submitonchange">
                    <md-icon class="error pb-5" v-show="failed">close</md-icon>
                  </slide-y-down-transition>
                  <slide-y-down-transition v-if="!submitonchange">
                    <md-icon class="success pb-5" v-show="passed">done</md-icon>
                  </slide-y-down-transition>
                </md-field>
              </ValidationProvider>

            </div>
          </div>
               <slot name="content"></slot>
        </md-card-content>

        <md-card-actions class="text-center">
          <md-button class="md-danger" @click="cancel()" v-if="cancelbutton" :disabled="isUploading">{{$t('cancel')}}</md-button>
          <md-button type="submit" class="md-success" :disabled="isUploading"  v-if="acceptbutton">
            <template v-if="isUploading">
              <md-icon class="md-icon-left">cloud_upload</md-icon>
              Uploading...
            </template>
            <template v-else>
              {{ buttonLabel }}
            </template>
          </md-button>
        </md-card-actions>
      </md-card>
    </form>
  </ValidationObserver>
</template>

<script>
import Vue from "vue"; // Importar Vue para usar Vue.set
import { SlideYDownTransition } from "vue2-transitions";
import { ValidationProvider, ValidationObserver, extend } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import { uploadImageAction, uploadVideoAction } from "@/pages/Products/actions"; // Importar la acción para subir la imagen y video
import Swal from "sweetalert2"; // Importar SweetAlert para notificaciones
import permissionsMixin from '@/mixins/permissionsMixin';


extend("required", required);

export default {
  mixins: [permissionsMixin],
  components: {
    SlideYDownTransition,
    ValidationProvider,
    ValidationObserver
  },
  props: {
    inputs: {
      type: Array,
      required: true,
      default: () => [],
    },
    requiredFields: {
      type: Array,
      default: () => [],
    },
    preloadedData: {
      type: Object,
      default: () => ({}),
    },
    acceptbutton: {
      type: Boolean,
      default: true,
    },
    submitonchange: {
      type: Boolean,
      default: false,
    },
    cancelbutton: {
      type: Boolean,
      default: true,
    },
    buttonLabel: {
      type: String,
      default: "Submit",
    },
    formTitle: {
      type: String,
    },
    avatarImg: {
      type: String,
      default: '/img/placeholder.jpg',  // Imagen por defecto para el avatar
    },
  },
    created(){
      if(!this.formData.image){
        //this.formData.image = 'https://gotakeit.s3.eu-west-1.amazonaws.com/1729869115963_IMG_0834.jpeg';
      }
        this.inputs.forEach((input) => {
          if (input.default !== undefined && !this.formData[input.name]) {
            Vue.set(this.formData, input.name, input.default);
          }
        });
    },
  data() {
    return {
      formData: { ...this.preloadedData },
      selectedImageFile: null, // Archivo de imagen seleccionado
      selectedVideoFile: null, // Archivo de video seleccionado
      isUploading: false, // Estado de carga de imagen
    };
  },
  methods: {
    getValidationRules(fieldName) {
      return this.requiredFields.includes(fieldName) ? "required" : "";
    },
    cancel(){
      this.$emit('cancel')
    },
    async submit() {    
      if(this.isDemo()){
        return;
      }
      // Verificamos si hay una imagen pendiente de subircon

      for (const input of this.inputs) {
        if (input.type === 'image' && this.selectedImageFile) {
          this.isUploading = true;
          const response = await uploadImageAction(this.selectedImageFile); // Subir imagen
          if (response.ok) {
            // Usar Vue.set para actualizar el campo reactivamente
            Vue.set(this.formData, input.name, response.data.data.Location); // URL de la imagen subida
            this.selectedImageFile = null; // Reseteamos el archivo de imagen seleccionado
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Oops...',
              text: this.$t('errorUploadingImage'),
            });
            this.isUploading = false;
            return;
          }
        }
        if (input.type === 'video' && this.selectedVideoFile) {
          this.isUploading = true;
          
          const response = await uploadVideoAction(this.selectedVideoFile); // Subir video
          
          if (response.ok) {
            // Usar Vue.set para actualizar el campo reactivamente
            Vue.set(this.formData, input.name, response.data.data.Location); // URL del video subido
            this.selectedVideoFile = null; // Reseteamos el archivo de video seleccionado
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Oops...',
              text: 'Error uploading video',
            });
            this.isUploading = false;
            return;
          }
        }
      }
      this.isUploading = false;
        
      // Emitimos el objeto con los datos del formulario
      this.$emit("formSubmitted", this.formData);
    },
    onFileChange(e, fieldName) {
      let files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;

      // Verificar que el archivo es una imagen válida
      const file = files[0];
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif'];

      if (!validImageTypes.includes(file.type)) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid file type',
          text: 'Please upload an image file (png, jpg, jpeg, gif).',
        });
        return;
      }

      this.selectedImageFile = file; // Guardamos la imagen seleccionada

      // Convertimos la imagen a base64 temporalmente para previsualización
      let reader = new FileReader();
      reader.onload = (event) => {
        // Usar Vue.set para actualizar el campo reactivamente
        Vue.set(this.formData, fieldName, event.target.result); // Actualizamos la previsualización del avatar o imagen
      };
      reader.readAsDataURL(file);
    },
    removeImage(fieldName) {
      // Usar Vue.set para eliminar la imagen de manera reactiva
      Vue.set(this.formData, fieldName, "");
      this.selectedImageFile = null; // Reiniciamos el archivo de imagen seleccionado
    },
    onVideoChange(e, fieldName) {
      let files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;

      // Verificar que el archivo es un video válido
      const file = files[0];
      const validVideoTypes = ['video/mp4', 'video/webm', 'video/ogg'];

      if (!validVideoTypes.includes(file.type)) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid file type',
          text: 'Please upload a video file (mp4, webm, ogg).',
        });
        return;
      }

      this.selectedVideoFile = file; // Guardamos el video seleccionado

      // Convertimos el video a URL temporalmente para previsualización
      let reader = new FileReader();
      reader.onload = (event) => {
        // Usar Vue.set para actualizar el campo reactivamente
        Vue.set(this.formData, fieldName, event.target.result); // Actualizamos la previsualización del video
      };
      reader.readAsDataURL(file);
    },
    removeVideo(fieldName) {
      // Usar Vue.set para eliminar el video de manera reactiva
      Vue.set(this.formData, fieldName, "");
      this.selectedVideoFile = null; // Reiniciamos el archivo de video seleccionado
    },
  },
};
</script>

<style scoped>
.video-container {
  text-align: center;
  padding: 20px;
  border: 2px dashed #ccc;
  border-radius: 8px;
  margin-bottom: 10px;
}

.video-placeholder {
  font-size: 48px;
  color: #ccc;
  margin-bottom: 10px;
}

.video-container p {
  margin: 0;
  color: #666;
}

.video-container video {
  border-radius: 8px;
  max-width: 100%;
}

.upload-loading {
  margin: 10px 0;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.loading-info {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
  color: #495057;
}

.loading-icon {
  margin-right: 8px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

.md-progress-bar {
  height: 8px;
  border-radius: 4px;
}
</style>
